import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Target, Filter, Info } from 'lucide-react';
import { MaterialityPoint } from '@/types/materiality';
import { materialityService } from '@/utils/materialityService';

interface MaterialityMatrixProps {}

export const MaterialityMatrix: React.FC<MaterialityMatrixProps> = () => {
  const [topics, setTopics] = useState<MaterialityPoint[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<MaterialityPoint | null>(null);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [loading, setLoading] = useState(true);

  // Fetch data from database
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log('Fetching materiality topics for matrix...');
        const topicsData = await materialityService.getMaterialityTopics();
        
        // Transform MaterialityTopic to MaterialityPoint for the matrix
        const matrixData: MaterialityPoint[] = topicsData.map(topic => ({
          id: topic.id,
          name: topic.name,
          category: topic.category,
          businessImpact: topic.businessImpact,
          stakeholderImportance: topic.stakeholderImportance,
          priority: topic.priority,
          quadrant: topic.quadrant,
          description: topic.description || '',
          stakeholders: [], // Will be populated separately if needed
          actions: [] // Will be populated separately if needed
        }));
        
        console.log('Transformed matrix data:', matrixData);
        setTopics(matrixData);
      } catch (error) {
        console.error('Error fetching materiality topics for matrix:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredTopics = topics.filter(topic => {
    if (filterCategory !== 'all' && topic.category !== filterCategory) return false;
    if (filterPriority !== 'all' && topic.priority !== filterPriority) return false;
    return true;
  });

  const quadrants = {
    'high-high': { name: 'High Business Impact, High Stakeholder Importance', color: '#ef4444' },
    'high-low': { name: 'High Business Impact, Low Stakeholder Importance', color: '#f97316' },
    'low-high': { name: 'Low Business Impact, High Stakeholder Importance', color: '#3b82f6' },
    'low-low': { name: 'Low Business Impact, Low Stakeholder Importance', color: '#6b7280' }
  };

  const getQuadrantColor = (quadrant: string) => {
    return quadrants[quadrant as keyof typeof quadrants]?.color || '#6b7280';
  };

  const getQuadrantName = (quadrant: string) => {
    return quadrants[quadrant as keyof typeof quadrants]?.name || 'Unknown Quadrant';
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Materiality Matrix
            </CardTitle>
            <CardDescription>
              Interactive visualization of business impact vs stakeholder importance
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-96 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-2"></div>
                <p className="text-sm text-gray-600">Loading materiality data...</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Materiality Matrix
          </CardTitle>
          <CardDescription>
            Interactive visualization of business impact vs stakeholder importance
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 mb-6">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              <span className="text-sm font-medium">Filter:</span>
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="environmental">Environmental</SelectItem>
                <SelectItem value="social">Social</SelectItem>
                <SelectItem value="governance">Governance</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="high">High Priority</SelectItem>
                <SelectItem value="medium">Medium Priority</SelectItem>
                <SelectItem value="low">Low Priority</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart
                margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  type="number" 
                  dataKey="businessImpact" 
                  name="Business Impact"
                  domain={[1, 5]}
                  tickCount={5}
                  label={{ value: 'Business Impact', position: 'bottom', offset: 0 }}
                />
                <YAxis 
                  type="number" 
                  dataKey="stakeholderImportance" 
                  name="Stakeholder Importance"
                  domain={[1, 5]}
                  tickCount={5}
                  label={{ value: 'Stakeholder Importance', angle: -90, position: 'left' }}
                />
                <Tooltip 
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload as MaterialityPoint;
                      return (
                        <div className="bg-white p-3 border rounded-lg shadow-lg">
                          <p className="font-semibold">{data.name}</p>
                          <p className="text-sm text-gray-600">{data.description}</p>
                          <p className="text-sm">Business Impact: {data.businessImpact}</p>
                          <p className="text-sm">Stakeholder Importance: {data.stakeholderImportance}</p>
                          <p className="text-sm">Priority: {data.priority}</p>
                          <p className="text-sm">Category: {data.category}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Scatter data={filteredTopics} onClick={(data) => setSelectedTopic(data)}>
                  {filteredTopics.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={getQuadrantColor(entry.quadrant)}
                      style={{ cursor: 'pointer' }}
                    />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(quadrants).map(([key, quadrant]) => (
              <div key={key} className="text-center p-3 border rounded-lg">
                <div 
                  className="w-4 h-4 rounded-full mx-auto mb-2" 
                  style={{ backgroundColor: quadrant.color }}
                ></div>
                <p className="text-xs font-medium">{quadrant.name}</p>
                <p className="text-lg font-bold">
                  {filteredTopics.filter(topic => topic.quadrant === key).length}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!selectedTopic} onOpenChange={() => setSelectedTopic(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="h-5 w-5" />
              {selectedTopic?.name}
            </DialogTitle>
            <DialogDescription>
              Detailed information about this materiality topic
            </DialogDescription>
          </DialogHeader>
          {selectedTopic && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Category</label>
                  <p className="text-sm">{selectedTopic.category}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Priority</label>
                  <Badge variant={selectedTopic.priority === 'high' ? 'destructive' : selectedTopic.priority === 'medium' ? 'default' : 'secondary'}>
                    {selectedTopic.priority}
                  </Badge>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Business Impact</label>
                  <p className="text-sm">{selectedTopic.businessImpact}/5</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Stakeholder Importance</label>
                  <p className="text-sm">{selectedTopic.stakeholderImportance}/5</p>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Description</label>
                <p className="text-sm mt-1">{selectedTopic.description}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Quadrant</label>
                <p className="text-sm mt-1">{getQuadrantName(selectedTopic.quadrant)}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}; 